import React, { useState, useEffect } from 'react';
import ExternalInstitutions from './ExternalInstitutions';
import { Plus, Building2, Users, CheckCircle, XCircle, Edit, Key, Eye, EyeOff, Lock, Unlock, Search, Filter, Download, Copy, Check, Upload, Image } from 'lucide-react';
import axios from 'axios';
import { compressImage } from '@/utils/imageCompressor';
import { formatDate } from '../../utils/dateTimeUtils';

const CollegeManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
  // Selected college for actions
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeCredentials, setCollegeCredentials] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form data for add/edit
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    address: '',
    college_code: '',
    logo: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    max_students: '',
    max_staff: '',
    max_yearly_admissions: '',
    max_yearly_staff: '',
    subscription_end_date: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/my-group`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupData(response.data);
      setColleges(response.data.colleges || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter colleges
  const filteredColleges = colleges.filter(college => {
    const matchesSearch = college.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         college.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         college.admin_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && college.status === 'active') ||
                         (statusFilter === 'blocked' && college.status === 'blocked');
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: colleges.length,
    active: colleges.filter(c => c.status === 'active').length,
    blocked: colleges.filter(c => c.status === 'blocked').length
  };

  // Handle Add College
  const handleAddCollege = async (e) => {
    e.preventDefault();
    
    // Validate validity date
    if (formData.subscription_end_date && groupData?.subscription?.end_date) {
      const collegeEnd = new Date(formData.subscription_end_date);
      const groupEnd = new Date(groupData.subscription.end_date);
      if (collegeEnd > groupEnd) {
        alert(`College validity cannot exceed Group validity (${formatDate(groupData.subscription.end_date)})`);
        return;
      }
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/groups/my-group/add-college`, {
        ...formData,
        max_students: formData.max_students ? parseInt(formData.max_students) : null,
        max_staff: formData.max_staff ? parseInt(formData.max_staff) : null,
        max_yearly_admissions: formData.max_yearly_admissions ? parseInt(formData.max_yearly_admissions) : null,
        max_yearly_staff: formData.max_yearly_staff ? parseInt(formData.max_yearly_staff) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('College added successfully!');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add college');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit College
  const handleEditCollege = async (e) => {
    e.preventDefault();
    
    // Validate validity date
    if (formData.subscription_end_date && groupData?.subscription?.end_date) {
      const collegeEnd = new Date(formData.subscription_end_date);
      const groupEnd = new Date(groupData.subscription.end_date);
      if (collegeEnd > groupEnd) {
        alert(`College validity cannot exceed Group validity (${formatDate(groupData.subscription.end_date)})`);
        return;
      }
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/groups/my-group/college/${selectedCollege.id}`, {
        name: formData.name || null,
        address: formData.address || null,
        college_code: formData.college_code || null,
        logo: formData.logo || null,
        admin_name: formData.admin_name || null,
        admin_email: formData.admin_email || null,
        admin_phone: formData.admin_phone || null,
        max_students: formData.max_students ? parseInt(formData.max_students) : null,
        max_staff: formData.max_staff ? parseInt(formData.max_staff) : null,
        max_yearly_admissions: formData.max_yearly_admissions ? parseInt(formData.max_yearly_admissions) : null,
        max_yearly_staff: formData.max_yearly_staff ? parseInt(formData.max_yearly_staff) : null,
        subscription_end_date: formData.subscription_end_date || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('College updated successfully!');
      setShowEditModal(false);
      setSelectedCollege(null);
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update college');
    } finally {
      setActionLoading(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (college) => {
    setSelectedCollege(college);
    setFormData({
      name: college.name || '',
      subdomain: college.subdomain || '',
      address: college.address || '',
      college_code: college.college_code || '',
      logo: college.logo || '',
      admin_name: college.admin_name || '',
      admin_email: college.admin_email || '',
      admin_phone: college.admin_phone || '',
      max_students: college.subscription?.max_students?.toString() || '',
      max_staff: college.subscription?.max_staff?.toString() || '',
      max_yearly_admissions: college.subscription?.max_yearly_admissions?.toString() || '',
      max_yearly_staff: college.subscription?.max_yearly_staff?.toString() || '',
      subscription_end_date: college.subscription?.end_date?.split('T')[0] || ''
    });
    setLogoPreview(college.logo || null);
    setShowEditModal(true);
  };

  // View Credentials
  const handleViewCredentials = async (college) => {
    setSelectedCollege(college);
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/groups/my-group/college/${college.id}/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollegeCredentials(response.data);
      setShowCredentialsModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to fetch credentials');
    } finally {
      setActionLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async (college) => {
    setSelectedCollege(college);
    setNewPassword(null);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/groups/my-group/college/${selectedCollege.id}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewPassword(response.data.new_password);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
      setShowResetPasswordModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  // Block/Unblock
  const handleToggleBlock = async (college) => {
    const action = college.status === 'active' ? 'block' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} "${college.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/groups/my-group/college/${college.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} college`);
    }
  };

  // Helpers
  const resetForm = () => {
    setFormData({
      name: '', subdomain: '', address: '', college_code: '', logo: '',
      admin_name: '', admin_email: '', admin_phone: '',
      max_students: '', max_staff: '', max_yearly_admissions: '', max_yearly_staff: '',
      subscription_end_date: groupData?.subscription?.end_date?.split('T')[0] || ''
    });
    setLogoPreview(null);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo size must be less than 5MB');
        return;
      }
      try {
        const result = await compressImage(file, { maxDim: 400, quality: 0.85 });
        setLogoPreview(result.dataUrl);
        setFormData({ ...formData, logo: result.dataUrl });
        console.log(`Logo compressed: ${result.width}x${result.height}, ~${result.sizeKB}KB`);
      } catch (err) {
        alert('Failed to process logo: ' + err.message);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>;
    }
    return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Blocked</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">College Management</h1>
          <p className="text-gray-600 mt-1">Manage colleges under {groupData?.name || 'your group'}</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          Add College
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Colleges</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Blocked</p>
              <p className="text-2xl font-bold text-red-600">{stats.blocked}</p>
            </div>
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, subdomain, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Colleges Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-4 font-semibold text-gray-700">College</th>
              <th className="text-left p-4 font-semibold text-gray-700">Subdomain</th>
              <th className="text-left p-4 font-semibold text-gray-700">Code</th>
              <th className="text-left p-4 font-semibold text-gray-700">Admin</th>
              <th className="text-left p-4 font-semibold text-gray-700">Limits</th>
              <th className="text-left p-4 font-semibold text-gray-700">Validity</th>
              <th className="text-left p-4 font-semibold text-gray-700">Status</th>
              <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredColleges.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-gray-500">
                  {searchTerm || statusFilter !== 'all' ? 'No colleges match your search' : 'No colleges added yet'}
                </td>
              </tr>
            ) : (
              filteredColleges.map((college) => (
                <tr key={college.id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {college.logo ? (
                        <img src={college.logo} alt={college.name} className="h-10 w-10 object-contain rounded border" />
                      ) : (
                        <Building2 className="h-10 w-10 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{college.name}</p>
                        {college.address && (
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">{college.address}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">/{college.subdomain}</code>
                  </td>
                  <td className="p-4">
                    {college.college_code ? (
                      <code className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-semibold">{college.college_code}</code>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-gray-900">{college.admin_name || '-'}</p>
                    <p className="text-xs text-gray-500">{college.admin_email || '-'}</p>
                  </td>
                  <td className="p-4">
                    <div className="text-xs">
                      <p>Students: {college.subscription?.max_students === -1 ? '∞' : college.subscription?.max_students || '-'}</p>
                      <p>Staff: {college.subscription?.max_staff === -1 ? '∞' : college.subscription?.max_staff || '-'}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sm">
                      {college.subscription?.end_date ? formatDate(college.subscription.end_date) : '-'}
                    </p>
                  </td>
                  <td className="p-4">{getStatusBadge(college.status)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditModal(college)}
                        className="p-2 hover:bg-blue-50 rounded text-blue-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleViewCredentials(college)}
                        className="p-2 hover:bg-gray-100 rounded text-gray-600"
                        title="View Credentials"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(college)}
                        className="p-2 hover:bg-yellow-50 rounded text-yellow-600"
                        title="Reset Password"
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleBlock(college)}
                        className={`p-2 hover:bg-gray-100 rounded ${college.status === 'active' ? 'text-red-600' : 'text-green-600'}`}
                        title={college.status === 'active' ? 'Block' : 'Unblock'}
                      >
                        {college.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add College Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Add New College</h2>
            </div>
            <form onSubmit={handleAddCollege} className="p-6 space-y-4">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">College Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Preview" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <label className="cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">College Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain *</label>
                  <input
                    type="text"
                    required
                    value={formData.subdomain}
                    onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., college-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">College Code</label>
                  <input
                    type="text"
                    value={formData.college_code}
                    onChange={(e) => setFormData({...formData, college_code: e.target.value.toUpperCase()})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., GNEC"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Admin Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.admin_name}
                      onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.admin_email}
                      onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Phone</label>
                    <input
                      type="tel"
                      value={formData.admin_phone}
                      onChange={(e) => setFormData({...formData, admin_phone: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Limits & Validity</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                    <input
                      type="number"
                      value={formData.max_students}
                      onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Staff</label>
                    <input
                      type="number"
                      value={formData.max_staff}
                      onChange={(e) => setFormData({...formData, max_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="-1 = Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Admissions</label>
                    <input
                      type="number"
                      value={formData.max_yearly_admissions}
                      onChange={(e) => setFormData({...formData, max_yearly_admissions: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Staff</label>
                    <input
                      type="number"
                      value={formData.max_yearly_staff}
                      onChange={(e) => setFormData({...formData, max_yearly_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validity End Date
                      {groupData?.subscription?.end_date && (
                        <span className="text-xs text-gray-500 ml-2">(Max: {formatDate(groupData.subscription.end_date)})</span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={formData.subscription_end_date}
                      onChange={(e) => setFormData({...formData, subscription_end_date: e.target.value})}
                      max={groupData?.subscription?.end_date?.split('T')[0]}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Adding...' : 'Add College'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit College Modal */}
      {showEditModal && selectedCollege && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Edit College</h2>
              <p className="text-sm text-gray-500">{selectedCollege.name}</p>
            </div>
            <form onSubmit={handleEditCollege} className="p-6 space-y-4">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">College Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Preview" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <label className="cursor-pointer px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Change
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">College Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                  <input
                    type="text"
                    value={formData.subdomain}
                    disabled
                    className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">College Code</label>
                  <input
                    type="text"
                    value={formData.college_code}
                    onChange={(e) => setFormData({...formData, college_code: e.target.value.toUpperCase()})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Admin Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                    <input
                      type="text"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                    <input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Phone</label>
                    <input
                      type="tel"
                      value={formData.admin_phone}
                      onChange={(e) => setFormData({...formData, admin_phone: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Limits & Validity</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Students</label>
                    <input
                      type="number"
                      value={formData.max_students}
                      onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Staff</label>
                    <input
                      type="number"
                      value={formData.max_staff}
                      onChange={(e) => setFormData({...formData, max_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Admissions</label>
                    <input
                      type="number"
                      value={formData.max_yearly_admissions}
                      onChange={(e) => setFormData({...formData, max_yearly_admissions: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Staff</label>
                    <input
                      type="number"
                      value={formData.max_yearly_staff}
                      onChange={(e) => setFormData({...formData, max_yearly_staff: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validity End Date
                      {groupData?.subscription?.end_date && (
                        <span className="text-xs text-gray-500 ml-2">(Max: {formatDate(groupData.subscription.end_date)})</span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={formData.subscription_end_date}
                      onChange={(e) => setFormData({...formData, subscription_end_date: e.target.value})}
                      max={groupData?.subscription?.end_date?.split('T')[0]}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedCollege(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && collegeCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Admin Credentials</h2>
              <p className="text-sm text-gray-500">{selectedCollege?.name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Email</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded">{collegeCredentials.email}</code>
                  <button
                    onClick={() => copyToClipboard(collegeCredentials.email)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded">
                    {showPassword ? collegeCredentials.password : '••••••••'}
                  </code>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(collegeCredentials.password)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button
                onClick={() => { setShowCredentialsModal(false); setCollegeCredentials(null); setShowPassword(false); }}
                className="w-full mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedCollege && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Reset Admin Password</h2>
              <p className="text-sm text-gray-500">{selectedCollege?.name}</p>
            </div>
            <div className="p-6">
              {newPassword ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700 font-medium mb-2">Password Reset Successfully!</p>
                    <p className="text-sm text-gray-600 mb-2">New Password:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">{newPassword}</code>
                      <button
                        onClick={() => copyToClipboard(newPassword)}
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowResetPasswordModal(false); setNewPassword(null); }}
                    className="w-full px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">Are you sure you want to reset the admin password for this college? A new password will be generated.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmResetPassword}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading ? 'Resetting...' : 'Yes, Reset Password'}
                    </button>
                    <button
                      onClick={() => setShowResetPasswordModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ExternalInstitutions />
    </div>
  );
};

export default CollegeManagement;
